<script setup lang="ts">
import {ref, watch} from "vue";
import {QEditor} from "quasar";
import Translator from "@/utils/common/Translator.ts";
import {storeToRefs} from "pinia";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {useThemeStore} from "@/stores/ThemeDataStore.ts";

const props = defineProps<{
  editor: QEditor | null | undefined;
  disable?: boolean;
}>();
const {darkMode} = storeToRefs(useNamaFlagsStore());
const themeStore = useThemeStore();
const lastColor = ref('var(--nama-font-color)');

function themeFontColor(): string {
  return darkMode.value ? themeStore.getFontInputDarkColor() : themeStore.getFontInputLightColor();
}

const pickerColor = ref(themeFontColor());
watch(darkMode, () => pickerColor.value = themeFontColor());
const colorChanged = ref(false);
let savedRange: Range | null = null;

function onPickerOpen() {
  colorChanged.value = false;
  const selection = window.getSelection();
  savedRange = selection && selection.rangeCount ? selection.getRangeAt(0).cloneRange() : null;
}

function onPickerClose() {
  const editor = props.editor;
  if (!colorChanged.value || !editor || !savedRange) return;
  colorChanged.value = false;

  (editor.getContentEl() as HTMLElement).focus();
  const selection = window.getSelection()!;
  selection.removeAllRanges();
  selection.addRange(savedRange);

  lastColor.value = pickerColor.value;
  document.execCommand('styleWithCSS', false, 'true');
  document.execCommand('foreColor', false, pickerColor.value);
}
</script>

<template>
  <q-btn-dropdown
    color="transparent"
    size="sm" dense
    :disable="disable || !editor"
    class="q-btn-dropdown-dense tw-h-fit"
    content-class="nama-richtext-color-menu"
    @before-show="onPickerOpen"
    @hide="onPickerClose"
    unelevated>
    <template #label>
      <div class="tw-flex tw-flex-col tw-items-center tw-leading-none tw-px-0.5">
        <span class="tw-font-bold tw-text-sm">A</span>
        <span class="tw-w-4 tw-h-1 tw-rounded-sm" :style="{backgroundColor: lastColor}"/>
      </div>
    </template>
    <q-tooltip>{{ Translator.translate('fontColor') }}</q-tooltip>
    <q-color
      v-model="pickerColor"
      default-view="palette"
      @change="colorChanged = true"
      format-model="hex"
      :dark="darkMode"
      class="nama-richtext-color-picker"/>
  </q-btn-dropdown>
</template>
